//imports for React and redux
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { updateAppState } from "../../actions/appstate";
import { getInsightDisplay } from "../../services/wizard.service";
import { buildViewDescriptor } from "./InsightPanel";

//imports for MUI
import { Grid, Box, Tooltip } from "@mui/material";

//imports for left subcomponents
import SmartDatePicker from "../SmartDatePicker";
import DbaPicker from "./subComponents/leftComponents/dbaPicker";
import PickUpDays from "./subComponents/leftComponents/pickUpDaysBlock";
import PickUpType from "./subComponents/leftComponents/pickUpTypeSelect";
import ClearComparisionPicker from "./subComponents/leftComponents/clearComparisionButton";
import ScopePicker from "./subComponents/leftComponents/scopeSelect";
import InfoButton from "./subComponents/leftComponents/infoButton";
import MarketPicker from "./subComponents/leftComponents/marketPicker";
import UnconstrainedPicker from "./subComponents/leftComponents/unconstrainedPicker";
import MarketCensusButton from "./subComponents/leftComponents/MarketCensusButton";
import EventType from "./subComponents/leftComponents/eventTypeSelect";
import CategoriesType from "./subComponents/leftComponents/categoriesTypeSelect";
import compareToPicker from "./subComponents/leftComponents/compareToPicker";
import SdlyButton from "./subComponents/leftComponents/SdlyButton";
import Sdly2019Btn from "./subComponents/leftComponents/Sdly2019Btn";
import SummaryDatePicker from "../SmartDatePicker/SummaryDatePicker";
import ExpandBtn from "./subComponents/leftComponents/ExpandButton";
import SegmentBtn from "./subComponents/leftComponents/SegmentButton";
import ToggleRoomTypePricing from "./subComponents/leftComponents/ToggleRoomTypePricing";
import ToggleRoomTypeRestrictions from "./subComponents/leftComponents/ToggleRoomTypeRestrictions";

//imports for right subcomponents
import GridViewButton from "./subComponents/rightComponents/gridViewButton";
import DownloadButton from "./subComponents/rightComponents/downloadButton";
import SendRatesButton from "./subComponents/rightComponents/sendRatesButton";
import UpdateRestrictionsButton from "./subComponents/rightComponents/updateRestrictionsButton";
import CapacityButton from "./subComponents/rightComponents/CapacityButton";
import OptionsButton from "./subComponents/rightComponents/optionsButton";
import CompareTo from "./subComponents/leftComponents/CompareTo";
import { TILES } from "../../app/config";
import { combineArr, in_array, getPermissions, dateDiff, getSeriesById, deepCopy } from "../../app/util";
import CompDatePicker from "../SmartDatePicker/CompDatePickerIndex";
import moment from "moment";
import SummaryPicker from "./subComponents/leftComponents/SummaryPicker";
import SdYears from "./subComponents/leftComponents/SdYears";
import TableType from "./subComponents/leftComponents/TableType";
import DayOfWeek from "./subComponents/leftComponents/DayOfWeek";
import MultiSegmentPicker from "./subComponents/leftComponents/MultiSegmentPicker";
import MultiSegmentPickerOTB from "./subComponents/leftComponents/MultiSegmentPickerOTB";
import PriceCalendarBtn from "./subComponents/rightComponents/PriceCalendarBtn";
import AirportCodes from "./subComponents/leftComponents/AirportCodes";
import BulkPushButton from "./subComponents/leftComponents/BulkPushButton";
import InfoButtonMarketSellRate from "./subComponents/leftComponents/InfoButtonMarketSellRate";
import RatePushMode from "./subComponents/leftComponents/RatePushMode";
import { useMergedControls } from "../../hooks/tile-merge-logics";
import FeederMarketButton from "./subComponents/leftComponents/FeederMarketButton";

function WorkspaceControls() {
    //needs to be implemented into redux stores, checks which view on
    const onChartView = false;
    // const mergedControls = useMergedControls();
    const { property_details, market_census_enabled } = useSelector(state => state.auth.profile);
    const { app_state, ui_state } = useSelector((state) => state.app.current_state);
    const { controlSupport } = app_state;
    const { active_tiles, workspace_view } = ui_state;
    const { sdlyOn, isRoomTypePricing, segmentType } = app_state.workspace_controls;
    const { date_from, date_until } = app_state.primary_stay_range;
    const { permissions, index } = getPermissions(property_details, app_state.property_id);
   
    const dispatch = useDispatch();
    const ratePushedEnabled = () => {
        if (ui_state.active_tiles[0] === 'pricing' && permissions && permissions.rate_push === 0) {
            return false;
        }
        return true;
    }
    let { data } = deepCopy(getSeriesById('GetFlights'));
    let airport_codes = (data && data.meta) ? data.meta.airport_codes : [];
   
    const hasViews = () => {
        if (TILES[active_tiles[0]].views.length < 2 && TILES[active_tiles[0]].type === "summary_report") {
            return true;
        }
        if (TILES[active_tiles[0]].views.length < 2 && TILES[active_tiles[0]].id === "flights") {
            return true;
        }
        if (TILES[active_tiles[0]].views.length < 2 && TILES[active_tiles[0]].type !== "report") {
            return false;
        }

        return true;
    }

    const hasWorkspaceControls = function (controlID) {
        const { active_tiles } = ui_state;
        let workspaceControls = [];
        let hasWorkspaceControls = false;
        active_tiles.map(tile => {
            if (TILES[tile] && TILES[tile].workspaceControls) {
                workspaceControls = combineArr(workspaceControls, TILES[tile].workspaceControls);
            }
        })
        workspaceControls.forEach(control => {
            if (controlID === control) {
                hasWorkspaceControls = true;
            }
        })
        return hasWorkspaceControls;
    }
    let navigation = {
        single_date: {
            type: "singleDate",
            range_segments: [],
            inputs: [],
            as_of_date: true,
            label: "Date",
        },
        range: {
            type: "range",
            range_segments: ["current_month", 29, 59, 89],
            inputs: ["date_from", "date_until"],
            as_of_date: true,
            label: "Range",
        },
        weeks: {
            type: "weeks",
            range_segments: [],
            inputs: ["date_from", "date_until"],
            as_of_date: true,
            label: "Weeks",
        },
        months: {
            type: "months",
            range_segments: [],
            inputs: ["date_from", "date_until"],
            as_of_date: true,
            label: "Months",
        },
    }
    let initialRangeType = app_state.workspace_controls.scopeType === "Dba" ? "single_date" : "range";

    let dateRange = (
        <Box className={"control-option "} sx={{ position: "relative" }}>
            <SmartDatePicker
                dateRange={{ date_from, date_until, as_of_date: app_state.as_of_date }}
                datePickerConfig={{
                    type: "primaryStayDates",
                    dateRangeKey: "primary_stay_range",
                    shiftBtns: true,
                    initialRangeType: initialRangeType,
                    navigation: {...navigation},
                }}
            />
        </Box>
    );
    if(in_array(["hotel_summary", "market_summary"],ui_state.active_tiles)){
        initialRangeType="months";
        navigation = {
            months: {
                type: "months",
                range_segments: [],
                inputs: ["date_from", "date_until"],
                as_of_date: true,
                label: "Months",
            },
        }
        dateRange = (
            <Box className={"control-option "} sx={{ position: "relative" }}>
                <SummaryDatePicker
                    dateRange={{ date_from: app_state.summary_stay_range.date_from,
                         date_until: app_state.summary_stay_range.date_until, 
                         as_of_date: app_state.summary_stay_range.as_of_date }}
                    datePickerConfig={{
                        type: "summaryStayDates",
                        dateRangeKey: "summary_stay_range",
                        shiftBtns: true,
                        initialRangeType: "months",
                        navigation: {...navigation},
                    }}
                />
            </Box>
        );
    }
    const compdateRange = (
        <Box className="comparison-range control-option" sx={{ position: "relative" }}>
            <CompDatePicker
                dateRange={{ ...app_state.comp_stay_range }}
                datePickerConfig={{
                    datepicker_name: "comparisonDates",
                    type: "primaryStayDates",
                    dateRangeKey: "comp_stay_range",
                    shiftBtns: true,
                    navigation: {
                        single_date: {
                            type: "singleDate",
                            range_segments: [],
                            inputs: [],
                            as_of_date: true,
                            label: "Date",
                        },
                        range: {
                            type: "range",
                            range_segments: ["current_month", 29, 59, 89],
                            inputs: ["date_from", "date_until"],
                            as_of_date: true,
                            label: "Range",
                        },
                        weeks: {
                            type: "weeks",
                            range_segments: [],
                            inputs: ["date_from", "date_until"],
                            as_of_date: true,
                            label: "Weeks",
                        },
                        months: {
                            type: "months",
                            range_segments: [],
                            inputs: ["date_from", "date_until"],
                            as_of_date: true,
                            label: "Months",
                        },
                    },
                }}
            />
        </Box>
    );

    let showPicker = (ui_state.active_tiles[0] === "revpar_index" || ui_state.active_tiles[0] === "flights_x") ? "dn" : "";
    //Adjusting width of control options area to fit
    let optionsWidth = 8.0;
    if (app_state.controlSupport.segmented_view) {
        optionsWidth = optionsWidth - 0.4;
    }
    if (ui_state.show_chart_icon) {
        optionsWidth = optionsWidth - 0.3;
    }
    if (ui_state.active_tiles[0] === "pricing") {
        optionsWidth = optionsWidth - 1;
    }
    if (ui_state.active_tiles[0] === "restrictions") {
        optionsWidth = optionsWidth - 1.1;
    }
    const getCurrentTile = () => {
        return ui_state.active_tiles.join("-");
    }
    const getSegmentedBtn = () => {
        if (ui_state.active_tiles && in_array(ui_state.active_tiles, ["pace"]) && ui_state.active_tiles.includes('market') && ui_state.active_tiles.length > 1){
            return (<></>);
        }

        // if (in_array(ui_state.active_tiles, ["market"])
        //     && in_array(ui_state.active_tiles, ["otb", "pickup", "pace", "compset", "market_sell_rates", "forecast", "pricing"])
        // ) {
        //     return (<></>);
        // }
        if(in_array(ui_state.active_tiles, ["budget"])){
            return (<></>);
        }
        if (in_array(ui_state.active_tiles, ["market"])
            && app_state.workspace_controls.scopeType === 'Dba'
        ) {
            return (<></>);
        }

        if (in_array(ui_state.active_tiles, ["pace"]) && app_state.workspace_controls.scopeType === 'Dba' && active_tiles.length > 1){
            return (<></>);
        }


        if (in_array(ui_state.active_tiles, ["pricing"])
            && in_array(ui_state.active_tiles, ["pickup", "otb",
                "forecast"])) {
            return (<Grid id={`multi-segment-picker-${getCurrentTile()}`} item className="multi-segment-picker otb-multi-segment-picker">{<MultiSegmentPickerOTB />}</Grid>)
        }

        if (in_array(ui_state.active_tiles, ["flights"])) {
        return (<Grid item id={`${getCurrentTile()}__multi-segment-picker`} className="multi-segment-picker">{<MultiSegmentPicker />}</Grid>)
    }

        if (in_array(ui_state.active_tiles, ["pace"])
            && in_array(ui_state.active_tiles, ["pickup", "otb",
                "forecast", "pricing"])) {
            return (<Grid id={`${getCurrentTile()}__multi-segment-picker`} item className="multi-segment-picker otb-multi-segment-picker">{<MultiSegmentPickerOTB />}</Grid>)
            // <Grid item>{<SegmentBtn />}</Grid>)
        }

        if (in_array(ui_state.active_tiles, ["pickup", "pace", "market", "otb",
            "forecast", "bi_pace_comparison", "bi_pickup_analysis",
            "bi_sensitivity_analysis", "bi_pickup_analysis"])
            && !in_array(ui_state.active_tiles, ["budget"])) {
            return (<Grid id={`${getCurrentTile()}__multi-segment-picker`} item className="multi-segment-picker otb-multi-segment-picker">{<MultiSegmentPickerOTB />}</Grid>)
            // <Grid item>{<SegmentBtn />}</Grid>)
        }

        if (in_array(ui_state.active_tiles, ["compset", "market_sell_rates"]) && active_tiles.length > 1
            && !in_array(ui_state.active_tiles, ["market", "flights", "budget", "restrictions"])) {
            return null;
            // return ( <Grid item>{<MultiSegmentPickerOTB />}</Grid> )

        }

        // if (active_tiles.length > 1 && in_array(ui_state.active_tiles, ["pricing"])
        //     && !in_array(ui_state.active_tiles, ["market", "compset", "market_sell_rates",
        //         "flights", "budget", "restrictions"])
        // ) {
        //     return (
        //         // <Grid item>{<SegmentBtn />}</Grid>
        //         <Grid item>
        //             {<MultiSegmentPickerOTB />}
        //         </Grid>
        //     )
        // }
        return null;
    }
    // Ask LodgIQ access gate (admin-controlled) — hides the AI insight trigger too.
    const askLodgiqEnabled = useSelector((s) => s.auth?.user?.ask_lodgiq_enabled) ?? true;
    const insightPrefFromLogin = useSelector((s) => s.auth?.user?.insight_display);
    const wsStore = useStore();

    // "Summarize this view": route by the user's insight_display preference —
    // "chat" (default) opens Ask LodgIQ in mini mode with the view descriptor;
    // "inline" opens the docked InsightPanel.
    const triggerViewInsight = () => {
        try {
            if (getInsightDisplay(insightPrefFromLogin) === "inline") {
                window.dispatchEvent(new CustomEvent("lq:view-insight"));
            } else {
                window.dispatchEvent(new CustomEvent("lq:open-chat", {
                    detail: { view: buildViewDescriptor(wsStore), intent: "summarize" },
                }));
            }
        } catch (e) { }
    };
    const changeDefaultMode = (expandedView) => {
        ui_state.expanded_view = !expandedView
        dispatch({
            type: "UPDATE_STATE",
            payload: {
                ui_state: {
                    ...ui_state
                }
            }
        });
    };
    return (
        <Box id="full_workspace" sx={{ display: "flex", }}>
            <Grid className="control-grid-item" id={`${getCurrentTile()}__expand-view-control`} sx={{ display: "flex", alignItems: "center", gap: 1, mb: "4px" }}>
                <Box onClick={() => changeDefaultMode(ui_state.expanded_view)} sx={{ cursor: "pointer" }}>
                    {ui_state.expanded_view ?
                        <Tooltip title="Standard" arrow>
                            <svg width="30" height="30" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.5" y="0.5" width="32" height="32" rx="16" stroke="#B2B2B2" />
                                <path d="M15.2897 11.5105V14.9405C15.2897 15.1305 15.1297 15.2905 14.9397 15.2905H11.5197C11.2097 15.2905 11.0497 14.9105 11.2697 14.6805L12.5997 13.3505L9.44973 10.2205C9.23973 10.0005 9.23973 9.66047 9.44973 9.45047C9.65973 9.24047 9.99973 9.24047 10.2197 9.45047L13.3597 12.5905L14.6897 11.2605C14.9097 11.0405 15.2997 11.2005 15.2997 11.5105H15.2897Z" fill="#333333" />
                                <path d="M18.0697 17.7105C17.8797 17.7105 17.7197 17.8705 17.7197 18.0605V21.4905C17.7197 21.8005 18.0997 21.9605 18.3297 21.7405L19.6597 20.4105L22.7997 23.5505C23.0097 23.7605 23.3497 23.7605 23.5597 23.5505C23.7697 23.3405 23.7697 23.0005 23.5597 22.7905L20.4197 19.6505L21.7497 18.3205C21.9697 18.1005 21.8097 17.7105 21.4997 17.7105H18.0697Z" fill="#333333" />
                                <path d="M14.9297 17.7105C15.1197 17.7105 15.2797 17.8705 15.2797 18.0605V21.4905C15.2797 21.8005 14.8997 21.9605 14.6697 21.7405L13.3397 20.4105L10.1997 23.5505C9.98973 23.7605 9.64973 23.7605 9.43973 23.5505C9.22973 23.3405 9.22973 23.0005 9.43973 22.7905L12.5797 19.6505L11.2497 18.3205C11.0297 18.1005 11.1897 17.7105 11.4997 17.7105H14.9297Z" fill="#333333" />
                                <path d="M17.7097 11.5105V14.9405C17.7097 15.1305 17.8697 15.2905 18.0597 15.2905H21.4797C21.7897 15.2905 21.9497 14.9105 21.7297 14.6805L20.3997 13.3505L23.5397 10.2105C23.7497 10.0005 23.7497 9.66047 23.5397 9.45047C23.3297 9.24047 22.9897 9.24047 22.7797 9.45047L19.6397 12.5905L18.3097 11.2605C18.0897 11.0405 17.6997 11.2005 17.6997 11.5105H17.7097Z" fill="#333333" />
                            </svg>
                        </Tooltip>
                        :
                        <Tooltip title="Expand" arrow>
                            <svg width="30" height="30" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.5" y="0.5" width="32" height="32" rx="16" stroke="#B2B2B2" />
                                <path d="M13.8155 10H10.3543C10.1581 10 10 10.1581 10 10.3543V13.8101C10 14.1317 10.3816 14.2897 10.6105 14.0662L11.9514 12.7254L15.1182 15.8922C15.3308 16.1048 15.6742 16.1048 15.8868 15.8922C16.0994 15.6797 16.0994 15.3363 15.8868 15.1237L12.7199 11.9514L14.0608 10.6105C14.2897 10.3816 14.1317 10 13.8155 10Z" fill="#333333" />
                                <path d="M23 19.1899V22.6457C23 22.8419 22.8419 23 22.6457 23H19.1899C18.8738 23 18.7157 22.6184 18.9392 22.3895L20.2801 21.0486L17.1132 17.8818C16.9006 17.6692 16.9006 17.3258 17.1132 17.1132C17.3258 16.9006 17.6692 16.9006 17.8818 17.1132L21.0486 20.2801L22.3895 18.9392C22.613 18.7157 23 18.8738 23 19.1899Z" fill="#333333" />
                                <path d="M10 19.1899V22.6457C10 22.8419 10.1581 23 10.3543 23H13.8101C14.1262 23 14.2843 22.6184 14.0608 22.3895L12.7199 21.0486L15.8868 17.8818C16.0994 17.6692 16.0994 17.3258 15.8868 17.1132C15.6742 16.9006 15.3308 16.9006 15.1182 17.1132L11.9514 20.2801L10.6105 18.9392C10.387 18.7157 10 18.8738 10 19.1899Z" fill="#333333" />
                                <path d="M19.1899 10H22.6457C22.8419 10 23 10.1581 23 10.3543V13.8101C23 14.1262 22.6184 14.2843 22.3895 14.0608L21.0486 12.7199L17.8818 15.8868C17.6692 16.0994 17.3258 16.0994 17.1132 15.8868C16.9006 15.6742 16.9006 15.3308 17.1132 15.1182L20.2801 11.9514L18.9392 10.6105C18.7157 10.387 18.8738 10 19.1899 10Z" fill="#333333" />
                            </svg>
                        </Tooltip>
                    }
                </Box>
                {askLodgiqEnabled && (
                    <Box
                        onClick={triggerViewInsight}
                        sx={{ cursor: "pointer", ml: "2px", display: "flex" }}
                    >
                        <Tooltip title="Summarize this view (AI)" arrow>
                            <svg width="30" height="30" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.5" y="0.5" width="32" height="32" rx="16" stroke="#B2B2B2" />
                                <path d="M14.5 8.5 C15.3 13.2 16.9 14.8 21.6 15.6 C16.9 16.4 15.3 18 14.5 22.7 C13.7 18 12.1 16.4 7.4 15.6 C12.1 14.8 13.7 13.2 14.5 8.5 Z" fill="#01a4ef" />
                                <path d="M22.3 8 C22.7 10.3 23.5 11.1 25.8 11.5 C23.5 11.9 22.7 12.7 22.3 15 C21.9 12.7 21.1 11.9 18.8 11.5 C21.1 11.1 21.9 10.3 22.3 8 Z" fill="#9ed00d" />
                                <path d="M21 18.6 C21.3 20.2 21.8 20.7 23.4 21 C21.8 21.3 21.3 21.8 21 23.4 C20.7 21.8 20.2 21.3 18.6 21 C20.2 20.7 20.7 20.2 21 18.6 Z" fill="#01a4ef" opacity="0.55" />
                            </svg>
                        </Tooltip>
                    </Box>
                )}
                <Box sx={{ pr: 1 }}>
                    <svg width="2" height="50" viewBox="0 0 2 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 0V55" stroke="url(#paint0_linear_169_26790)" />
                        <defs>
                            <linearGradient id="paint0_linear_169_26790" x1="1.5" y1="0" x2="1.5" y2="55" gradientUnits="userSpaceOnUse">
                                <stop stop-color="#B2B2B2" stop-opacity="0" />
                                <stop offset="0.5" stop-color="#B2B2B2" />
                                <stop offset="1" stop-color="#B2B2B2" stop-opacity="0" />
                            </linearGradient>
                        </defs>
                    </svg>
                </Box>
            </Grid>

            <Grid
                id="workspace_left"
                elevation={1}
                container
                spacing={0}
                sx={{
                    mb: 0,
                    pt: 0,
                    pb: 0,
                    mt: 0,
                    display: { xs: "none", md: "flex" },
                }}
            >
                {
                    getSegmentedBtn()
                }

                {hasWorkspaceControls("toggleRoomTypePricing") && ui_state.active_tiles.length === 1 && workspace_view === "table" ? (
                    <Grid id={`${getCurrentTile()}__toggle-room-rype-pricing`} item className="control-grid-item toggle-room-rype-pricing">
                        <ToggleRoomTypePricing />
                    </Grid>
                ) : null}

                {/* {(in_array(ui_state.active_tiles, ["restrictions"]) && permissions.restriction_d && permissions.restriction_d.restriction_room_type === true) ? ( */}
                {(in_array(ui_state.active_tiles, ["restrictions"]) && permissions.restriction_d) ? (
                    <Grid id={`${getCurrentTile()}__toggle-room-rype-restriction`} item className="control-grid-item toggle-room-rype-restriction" >
                        <ToggleRoomTypeRestrictions />
                    </Grid>
                ) : null}

                {in_array(ui_state.active_tiles, ["otb_v2_multi_segment"]) ? (
                    <Grid id={`${getCurrentTile()}__multi-segment-picker`} item className="control-grid-item multi-segment-picker">
                        <MultiSegmentPicker />
                    </Grid>
                ) : null}
                {!in_array(ui_state.active_tiles, ["flights", "group_iq_reports_x"]) ?(<Grid id={`${getCurrentTile()}__date-range`} item className={showPicker}>
                    {dateRange}
                </Grid>) : (<Grid id={`${getCurrentTile()}__date-range`} item className={showPicker} style={{"visibility":"hidden", width:"0px"}}>
                    {dateRange}
                </Grid>) }
                

                <Grid  item sx={{ height: 57, overflowY: 'hidden' }}>
                    <Grid container className="control-scroll" sx={{ ':hover': { overflowX: 'auto' }, overflow: 'hidden' }} flexWrap="nowrap" alignContent="flex-start">
                        {in_array(ui_state.active_tiles, ["revpar_index"]) ? (
                            <Grid id={`${getCurrentTile()}__table-type`} item className="control-grid-item table-type">
                                <TableType />
                            </Grid>
                        ) : null}
                        {ui_state.active_tiles[0] === "events" && <Grid id={`${getCurrentTile()}__event-type`} item className="control-grid-item event-type">
                            <EventType />
                        </Grid>}

                        {ui_state.active_tiles[0] === "events" && <Grid id={`${getCurrentTile()}__categories-types`} item className="control-grid-item categories-types">
                            <CategoriesType />
                        </Grid>}

                        {in_array(ui_state.active_tiles, ["bi_sensitivity_analysis", "pace"]) ? (
                            <Grid id={`${getCurrentTile()}__comparison-date-picker`} item className="control-grid-item comparison-date-picker">
                                {compdateRange}
                            </Grid>
                        ) : null}

                        {in_array(ui_state.active_tiles, ["pickup", "market", "otb", "otb_v2_multi_segment", "hotel_summary", "market_summary", "bi_pace_comparison", "bi_pickup_analysis"]) && !ui_state.active_tiles.includes('pace') ? (
                            <>
                                {/* LRV-880 remove x from sd-year class */}
                                <Grid id={`${getCurrentTile()}__sd-year`} item className="control-grid-item sd-yearx">
                                    <SdYears />
                                </Grid>
                            </>

                        ) : <></>}


                        {in_array(ui_state.active_tiles, ["flights"]) ? (
                            <Grid id={`${getCurrentTile()}__airport-codes`} item className="control-grid-item airport-codes">
                                <AirportCodes />
                            </Grid>
                        ) : null}

                        {/* LRV-1094 START */}
                        {(in_array(ui_state.active_tiles, ["flights"]) && permissions.flights === 1 && airport_codes.length)? (
                            <Grid id={`${getCurrentTile()}__feeder-market`} item className="control-grid-item feeder-market">
                                <FeederMarketButton />
                            </Grid>
                        ) : null}
                        {/* LRV-1094 END */}

                        {(in_array(ui_state.active_tiles, ["restrictions"]) && app_state.workspace_controls.isRestrictionsRoomType !== "product" && permissions.restrictions ===1) ? (
                            <><Box className="bulk-push-button" display="flex">
                                
                                <BulkPushButton />

                            </Box></>) : null}
                        {/* {(in_array(ui_state.active_tiles, ["pricing"]) && app_state.workspace_controls.isRoomTypePricing ===true ) ? (
                        <><Box display="flex">
                            
                            <BulkPushButton />

                        </Box></>) : null} */}
                        {/* {hasWorkspaceControls("toggleRoomTypePricing") && ui_state.active_tiles.length ===1 && workspace_view === "table" ? (
                            <Grid item className="control-grid-item toggle-room-rype-pricing">
                                <ToggleRoomTypePricing />
                            </Grid>
                        ) : null} */}

                        {/* {(in_array(ui_state.active_tiles, ["restrictions"]) && permissions.restriction_d && permissions.restriction_d.restriction_room_type === true) ? (
                            <Grid item className="control-grid-item toggle-room-rype-restriction" >
                                <ToggleRoomTypeRestrictions />
                            </Grid>
                        ) : null} */}


                        {
                            // in_array(ui_state.active_tiles, ["otb", "otb_v2_multi_segment"]) && sdlyOn === true ? (
                            //     <Grid item className="control-grid-item sd-year">
                            //         {/* <Sdly2019Btn /> */}
                            //         <SdYears />
                            //     </Grid>
                            // ) : null
                        }

                        {/* {in_array(ui_state.active_tiles, ["otb_v2_multi_segment"]) ? (
                            <Grid item className="control-grid-item multi-segment-picker">
                                <MultiSegmentPicker />
                            </Grid>
                        ) : null} */}


                        {in_array(ui_state.active_tiles, ["bi_sensitivity_analysis"]) ? (
                            <Grid id={`${getCurrentTile()}__day-of-week`} item className="control-grid-item day-of-week">
                                <DayOfWeek />
                            </Grid>
                        ) : null}

                        {in_array(ui_state.active_tiles, ["market", "pickup", "bi_pickup_analysis"]) && app_state.workspace_controls.scopeType !== "Dba" ? (
                            <Grid id={`${getCurrentTile()}__pick-up-days`} item className="control-grid-item pick-up-days">
                                <PickUpDays />
                            </Grid>
                        ) : <></>
                        }

                        {(ui_state.active_tiles[0] === "hotel_summary" || ui_state.active_tiles[0] === "market_summary") ? (
                            <Grid id={`${getCurrentTile()}__summary-picker`} item className="control-grid-item summary-picker">
                                <SummaryPicker />
                            </Grid>
                        ) : null}

                        {in_array(ui_state.active_tiles, ["pickup", "bi_pickup_analysis"]) && <Grid id={`${getCurrentTile()}__pick-up-type`} item className="control-grid-item pick-up-type">
                            <PickUpType />
                        </Grid>}

                        {
                            in_array(ui_state.active_tiles, ["compset", "market", "pace"])
                                && !in_array(ui_state.active_tiles, ["otb", "pickup", "forecast", "market_sell_rates", "pricing", "budget"])
                                ? (<Grid id={`${getCurrentTile()}__scope-picker`} item className="control-grid-item scope-picker">
                                    <ScopePicker />
                                </Grid>
                                ) : null}

                        { (in_array(ui_state.active_tiles, ["compset", "market", "pace"]) && app_state.workspace_controls.scopeType === "Dba" || in_array(ui_state.active_tiles, ["bi_sensitivity_analysis"]))  && <Grid id={`${getCurrentTile()}__dba-picker`} item className="control-grid-item dba-picker">
                            <DbaPicker />
                        </Grid>}

                        {in_array(ui_state.active_tiles, ["market_summary", "market", "market_sell_rates"]) && app_state.selected_market ? (<Grid id={`${getCurrentTile()}__market-picker`} item className="control-grid-item market-picker">
                            <MarketPicker />
                        </Grid>
                        ) : null}

                        {in_array(ui_state.active_tiles, ["rm_room_type_ratesend_status",
                            "room_type_ratesend_status_date_range", "restrictions_push_status", "DailyRatesendStatus", 'plr_logs']) ? (<Grid id={`${getCurrentTile()}__rate-push-mode`} item className="control-grid-item rate-push-mode">
                                <RatePushMode />
                            </Grid>
                        ) : null}

                        {in_array(ui_state.active_tiles, ["market"]) ? (
                            <Grid id={`${getCurrentTile()}__unconstrained-picker`} item className="control-grid-item unconstrained-picker">
                                <UnconstrainedPicker />
                            </Grid>
                        ) : null}

                        {in_array(ui_state.active_tiles, ["market"]) && market_census_enabled ? (
                            <Grid id={`${getCurrentTile()}__market-census-button`} item className="control-grid-item market-census-button">
                                <MarketCensusButton />
                            </Grid>
                        ) : null}

                        {in_array(ui_state.active_tiles, ["pace", "bi_sensitivity_analysis"]) ? (
                            <Grid id={`${getCurrentTile()}__clear-comparison-btn`} item className="control-grid-item clear-comparison-btn">
                                <ClearComparisionPicker />
                            </Grid>) : null}

                        <Grid id={`${getCurrentTile()}__info-button`} item className="control-grid-item info-button">
                            {ui_state.active_tiles[0] === "marketx" || ui_state.active_tiles[0] === "market_summaryx" ? (
                                <InfoButton />
                            ) : null}
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item sx={{ ml: 'auto', display: "flex", alignItems: "flex-end", paddingBottom: "4px" }}>
                    <Box
                        id="workspace_right"
                        display="flex"
                        flexDirection="row-reverse"
                    //sx={{ pt: 0.7 }}
                    >
                        {in_array(ui_state.active_tiles, ["restrictions"]) && permissions.restrictions === 1 ? (
                            <><Box display="flex">
                                <UpdateRestrictionsButton getCurrentTile={getCurrentTile} />
                                {/* Only the Hotel data level has a real VCap toggle — Product/Room Type
                                    always behave as VCap-on (see TableRestrictionsProduct.js showVCap). */}
                                {app_state.workspace_controls.isRestrictionsRoomType === false && <CapacityButton />}
                                <GridViewButton />

                            </Box></>) : null}
                        <Box display="flex" sx={{ cursor: app_state.data_load ? 'not-allowed' : 'auto' }}>
                            {!in_array(ui_state.active_tiles, ["group_iq_reports"]) && hasViews() && <GridViewButton />}
                            {(ratePushedEnabled() && ui_state.active_tiles.length === 1 && hasWorkspaceControls("pricing_calendar_btn") && !isRoomTypePricing) ? (

                                <PriceCalendarBtn getCurrentTile={getCurrentTile} />

                            ) : null}
                        </Box>

                        <Box>
                            {" "}
                            {ui_state.active_tiles[0] === "events" ? (
                                <OptionsButton getCurrentTile={getCurrentTile} />
                            ) : null}{" "}
                        </Box>
                    </Box>
                </Grid>
            </Grid>

        </Box>
    );
}

export default WorkspaceControls;
